import { BEIGE_500, BEIGE_700, GRAY_200, GRAY_600, GRAY_900, WHITE } from 'constants/styling/theme'
import { Check } from '@mui/icons-material'

import Box from '@mui/material/Box'
import { FC } from 'react'
import { MessageAction } from 'models/conversations'
import Stack from '@mui/material/Stack'
import { StatusPill } from './StatusPill'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

interface Props {
  actions: MessageAction[]
  selected: string | null
  isLocked: boolean
  onSelect: (action: MessageAction) => void
}

export const OrderPickerList: FC<Props> = ({ actions, selected, isLocked, onSelect }) => {
  const { t } = useTranslation(['chat'])

  const pick = (action: MessageAction) => {
    if (isLocked) return
    onSelect(action)
  }

  return (
    <Stack
      data-testid="order-picker-list"
      role="list"
      sx={{ border: `1px solid ${GRAY_200}`, borderRadius: '1.2rem', overflow: 'hidden', backgroundColor: WHITE }}
    >
      {actions.map((action, index) => {
        const isSelected = selected === action.value
        const isDisabled = isLocked && !isSelected

        return (
          <Box
            key={`${action.value}-${index}`}
            role="listitem"
            sx={{ borderTop: index > 0 ? `1px solid ${GRAY_200}` : undefined }}
          >
            <Stack
              direction="row"
              alignItems="center"
              gap="1.2rem"
              role="button"
              tabIndex={isDisabled ? -1 : 0}
              aria-disabled={isLocked}
              aria-pressed={isSelected}
              onClick={() => pick(action)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter' && event.key !== ' ') return
                event.preventDefault()
                pick(action)
              }}
              sx={{
                padding: '1rem 1.4rem',
                backgroundColor: isSelected ? BEIGE_500 : WHITE,
                cursor: isDisabled ? 'default' : 'pointer',
                opacity: isDisabled ? 0.5 : 1,
                transition: 'background-color 0.15s',
                '&:hover': isDisabled || isSelected ? undefined : { backgroundColor: GRAY_200 },
                '&:focus-visible': { outline: `2px solid ${BEIGE_700}`, outlineOffset: '-2px' },
              }}
            >

              <Stack gap="0.1rem" sx={{ flex: 1, minWidth: 0 }}>

                <Typography variant="text-sm" fontWeight={600} color={GRAY_900} noWrap>
                  {action.label}
                </Typography>

                {!!action.sublabel &&
                  <Typography variant="text-xs" color={GRAY_600} noWrap>
                    {action.sublabel}
                  </Typography>
                }

              </Stack>

              {!!action.status && <StatusPill status={action.status} compact />}

              {isSelected &&
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  title={t('chat:suggestions.selected')}
                  sx={{ flexShrink: 0, width: '1.8rem', height: '1.8rem', borderRadius: '999px', backgroundColor: BEIGE_700, color: GRAY_900 }}
                >
                  <Check sx={{ fontSize: '1.2rem' }} />
                </Stack>
              }

            </Stack>
          </Box>
        )
      })}
    </Stack>
  )
}
